import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AppModal } from '../components/AppModal';
import { PageHead } from '../components/layout';
import { SearchSelect } from '../components/SearchSelect';
import { useActiveCompanyId, useCompanies } from '../lib/queries/companies';
import {
  useAllowlist,
  useCreateAllowlistEntry,
  useCreateEmailRoute,
  useDeleteAllowlistEntry,
  useDeleteEmailRoute,
  useDiscardMessage,
  useEmailRoutes,
  useMailboxStatus,
  useQuarantine,
  useReleaseMessage,
} from '../lib/queries/email-intake';

export const Route = createFileRoute('/settings/email-intake')({
  component: EmailIntakeSettingsPage,
});

export function EmailIntakeSettingsPage() {
  const companyId = useActiveCompanyId();
  const { data: companies = [] } = useCompanies();
  const { data: status } = useMailboxStatus(companyId);
  const { data: routes = [], isPending: routesPending } = useEmailRoutes(companyId);
  const { data: allowlist = [], isPending: allowlistPending } = useAllowlist(companyId);
  const deleteRoute = useDeleteEmailRoute(companyId);
  const deleteEntry = useDeleteAllowlistEntry(companyId);
  const { data: quarantine = [] } = useQuarantine(companyId);
  const release = useReleaseMessage(companyId);
  const discard = useDiscardMessage(companyId);
  const [addRoute, setAddRoute] = useState(false);
  const [addEntry, setAddEntry] = useState(false);

  const companyName = (id: string) => {
    const c = companies.find((x) => x.id === id);
    return c ? `${c.code} ${c.nameTh}` : id;
  };

  return (
    <div className="page">
      <PageHead crumbs={['ระบบ', 'รับเอกสารทางอีเมล']} title="รับเอกสารทางอีเมล (Email Intake)" />

      <div className="card" style={{ padding: 12, marginBottom: 16 }}>
        <span className={`badge ${status?.enabled ? 'positive' : 'negative'}`}>
          {status?.enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
        </span>{' '}
        <span className="muted">กล่องจดหมาย: {status?.mailbox ?? '—'}</span>{' '}
        <span className="muted">
          โพลล์ล่าสุด:{' '}
          {status?.lastPollAt ? new Date(status.lastPollAt).toLocaleString('th-TH') : '—'}
        </span>
        {status?.lastError && (
          <div className="muted" style={{ color: 'var(--negative)', fontSize: 12 }}>
            ข้อผิดพลาดล่าสุด: {status.lastError}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: '8px 0' }}>เส้นทาง (alias → บริษัท)</h3>
        <button className="btn sm primary" onClick={() => setAddRoute(true)}>
          เพิ่มเส้นทาง
        </button>
      </div>
      <div className="tbl-shell" style={{ marginBottom: 24 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 200 }}>alias</th>
              <th>บริษัทปลายทาง</th>
              <th style={{ width: 90 }}>สถานะ</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {routesPending ? (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : routes.length === 0 ? (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  ยังไม่มีเส้นทาง
                </td>
              </tr>
            ) : (
              routes.map((r) => (
                <tr key={r.id}>
                  <td className="code">
                    <b>{r.alias}</b>
                  </td>
                  <td>{companyName(r.companyId)}</td>
                  <td className="muted">{r.active ? 'ใช้งาน' : 'ปิด'}</td>
                  <td>
                    <button
                      className="btn sm ghost"
                      onClick={() => void deleteRoute.mutateAsync(r.id)}
                    >
                      ลบ
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: '8px 0' }}>ผู้ส่งที่เชื่อถือ (allowlist)</h3>
        <button className="btn sm primary" onClick={() => setAddEntry(true)}>
          เพิ่มผู้ส่ง
        </button>
      </div>
      <div className="tbl-shell">
        <table className="tbl">
          <thead>
            <tr>
              <th>รูปแบบ</th>
              <th style={{ width: 160 }}>ขอบเขต</th>
              <th>หมายเหตุ</th>
              <th style={{ width: 90 }}>สถานะ</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {allowlistPending ? (
              <tr>
                <td colSpan={5} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : allowlist.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  ยังไม่มีผู้ส่งที่เชื่อถือ
                </td>
              </tr>
            ) : (
              allowlist.map((a) => (
                <tr key={a.id}>
                  <td className="code">{a.pattern}</td>
                  <td className="muted">{a.companyId ? companyName(a.companyId) : 'ทุกบริษัท'}</td>
                  <td className="muted">{a.note ?? '—'}</td>
                  <td className="muted">{a.active ? 'ใช้งาน' : 'ปิด'}</td>
                  <td>
                    <button
                      className="btn sm ghost"
                      onClick={() => void deleteEntry.mutateAsync(a.id)}
                    >
                      ลบ
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '24px 0 8px' }}>กักกัน (quarantine)</h3>
      <div className="tbl-shell">
        <table className="tbl">
          <thead>
            <tr>
              <th>จาก</th>
              <th>เรื่อง</th>
              <th style={{ width: 140 }}>เหตุผล</th>
              <th style={{ width: 220 }} />
            </tr>
          </thead>
          <tbody>
            {quarantine.length === 0 ? (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  ไม่มีรายการกักกัน
                </td>
              </tr>
            ) : (
              quarantine.map((m) => (
                <tr key={m.id}>
                  <td>{m.fromAddress}</td>
                  <td>{m.subject ?? '—'}</td>
                  <td className="muted">{m.reason ?? '—'}</td>
                  <td>
                    <button className="btn sm" onClick={() => void release.mutateAsync(m.id)}>
                      ปล่อยกลับเข้าระบบ
                    </button>{' '}
                    <button
                      className="btn sm ghost"
                      style={{ color: 'var(--negative)' }}
                      onClick={() => void discard.mutateAsync(m.id)}
                    >
                      ทิ้ง
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {addRoute && <RouteModal onClose={() => setAddRoute(false)} />}
      {addEntry && <AllowlistModal onClose={() => setAddEntry(false)} />}
    </div>
  );
}

function RouteModal({ onClose }: { onClose: () => void }) {
  const companyId = useActiveCompanyId();
  const { data: companies = [] } = useCompanies();
  const create = useCreateEmailRoute(companyId);
  const [alias, setAlias] = useState('');
  const [targetCompany, setTargetCompany] = useState(companyId ?? '');
  const companyOptions = companies
    .filter((c): c is typeof c & { id: string } => !!c.id)
    .map((c) => ({ value: c.id, label: `${c.code} ${c.nameTh}` }));

  async function save() {
    await create.mutateAsync({ alias, companyId: targetCompany, active: true });
    onClose();
  }
  return (
    <AppModal
      title="เพิ่มเส้นทาง (alias → บริษัท)"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={!alias || !targetCompany || create.isPending}
            onClick={() => void save()}
          >
            บันทึก
          </button>
        </>
      }
    >
      <label className="muted" style={{ fontSize: 13 }}>
        alias (เช่น dcc จาก ap+dcc@…)
      </label>
      <input
        className="input"
        value={alias}
        onChange={(e) => setAlias(e.target.value)}
        style={{ width: '100%', marginBottom: 12 }}
      />
      <label className="muted" style={{ fontSize: 13 }}>
        บริษัทปลายทาง
      </label>
      <SearchSelect
        value={targetCompany}
        onChange={setTargetCompany}
        options={companyOptions}
        getLabel={(v) => companyOptions.find((o) => o.value === v)?.label ?? v}
        ariaLabel="บริษัทปลายทาง"
      />
    </AppModal>
  );
}

function AllowlistModal({ onClose }: { onClose: () => void }) {
  const companyId = useActiveCompanyId();
  const create = useCreateAllowlistEntry(companyId);
  const [pattern, setPattern] = useState('');
  const [note, setNote] = useState('');
  const [global, setGlobal] = useState(false);

  async function save() {
    await create.mutateAsync({
      pattern,
      companyId: global ? null : (companyId ?? null),
      vendorId: null,
      note: note || null,
      active: true,
    });
    onClose();
  }
  return (
    <AppModal
      title="เพิ่มผู้ส่งที่เชื่อถือ"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={!pattern || create.isPending}
            onClick={() => void save()}
          >
            บันทึก
          </button>
        </>
      }
    >
      <label className="muted" style={{ fontSize: 13 }}>
        รูปแบบ (เช่น billing@pea.co.th หรือ @pea.co.th)
      </label>
      <input
        className="input"
        value={pattern}
        onChange={(e) => setPattern(e.target.value)}
        style={{ width: '100%', marginBottom: 12 }}
      />
      <label
        style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12 }}
      >
        <input type="checkbox" checked={global} onChange={(e) => setGlobal(e.target.checked)} />{' '}
        ใช้ทุกบริษัท (global)
      </label>
      <label className="muted" style={{ fontSize: 13 }}>
        หมายเหตุ
      </label>
      <input
        className="input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{ width: '100%' }}
      />
    </AppModal>
  );
}
